import 'react-cmdk/dist/cmdk.css'
import '../../assets/react-cmdk-override.css'
import CommandPalette, { getItemIndex } from 'react-cmdk'
import React, { useState, useEffect, useMemo } from 'react'
import { getDataByGlobalSearchParam } from './api/global-search-api'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import LocationIcon from '@material-ui/icons/PlaceOutlined'
import DeviceHub from '@material-ui/icons/SettingsRemoteOutlined'
import { mdiFloorPlan } from '@mdi/js'
import Icon from '@mdi/react'
import { Highlighted } from './components/common/highlighted'
import { getOS } from '../../utils'
import { RouterIcon } from 'lucide-react'

export default function GlobalSearch() {
    const [page, setPage] = useState('root')
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState('')

    const { data } = useQuery(
        [search],
        () => getDataByGlobalSearchParam(search, 10),
        {
            refetchOnWindowFocus: false
        }
    )

    const os = getOS()

    const history = useHistory()
    const { locale, theme } = useSelector(state => state.main)
    const classes = useStyles()

    const handleOptionClick = path => {
        setSearch('')
        history.push(path)
    }

    const handleSearchClick = () => {
        setSearch('')
        setOpen(true)
    }

    const filteredItems = useMemo(() => {
        if (data) {
            const locationOptions = {
                heading: `${locale.locations.locations}`,
                id: 'locations',
                items: data.locations.map(item => ({
                    id: item.id,
                    title: item.name,
                    icon: () => (
                        <LocationIcon style={{ color: theme.primary }} />
                    ),
                    onClick: () => {
                        handleOptionClick(`/locations/${item.id}/overview`)
                    }
                }))
            }

            const deviceOptions = {
                heading: `${locale.locations.devicesListTitle}`,
                id: 'devices',
                items: data.devices.map(item => ({
                    id: item.id,
                    title: item.name,
                    icon: () => <DeviceHub style={{ color: theme.primary }} />,
                    onClick: () => {
                        handleOptionClick(`/devices/${item.id}`)
                    }
                }))
            }

            const floorPlansOptions = {
                heading: `${locale.locations.floorPlan}`,
                id: 'floor_plans',
                items: data.floor_plans.map(item => ({
                    id: `fp-${item.id}`,
                    title: item.name,
                    icon: () => (
                        <Icon
                            path={mdiFloorPlan}
                            style={{
                                color: theme.primary,
                                height: 24,
                                width: 24
                            }}
                        />
                    ),
                    onClick: () => {
                        handleOptionClick(`/locations/${item.id}/floor-plan`)
                    }
                }))
            }

            const networkDevicesOptions = {
                heading: `${locale.locations.storageSectionNetworkDevicesTitle}`,
                id: 'network_devices',
                items: data.network_devices.map(item => ({
                    id: `nd-${item.id}`,
                    title: item.serial_number,
                    icon: () => <RouterIcon size={24} color={theme.primary} />,
                    onClick: () => {
                        handleOptionClick(`/network-devices/${item.id}`)
                    }
                }))
            }

            return [
                locationOptions,
                deviceOptions,
                floorPlansOptions,
                networkDevicesOptions
            ]
        }

        return []
    }, [data])

    useEffect(() => {
        function handleKeyDown(e) {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                e.stopPropagation()

                setOpen(open => {
                    return !open
                })
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    return (
        <>
            <Button className={classes.btnSearch} onClick={handleSearchClick}>
                <div className={classes.btnInfo}>
                    <SearchIcon style={{ color: theme.secondary }} />
                    <div className={classes.mainBtnInfo}>
                        {locale.common.search}
                    </div>
                </div>
                <span className={classes.additonalBtnInfo}>{`${
                    os === 'Mac OS' ? '⌘' : 'ctrl'
                } + K`}</span>
            </Button>

            <CommandPalette
                onChangeSearch={setSearch}
                onChangeOpen={setOpen}
                search={search}
                isOpen={open}
                page={page}
            >
                <CommandPalette.Page id="root">
                    {filteredItems.length ? (
                        filteredItems.map(list => (
                            <CommandPalette.List
                                key={list.id}
                                heading={list.heading}
                            >
                                {list.items.map(({ id, ...rest }) => {
                                    return (
                                        <CommandPalette.ListItem
                                            key={id}
                                            index={getItemIndex(
                                                filteredItems,
                                                id
                                            )}
                                            showType={false}
                                            {...rest}
                                        >
                                            <Highlighted
                                                text={rest.title}
                                                highlight={search}
                                            />
                                        </CommandPalette.ListItem>
                                    )
                                })}
                            </CommandPalette.List>
                        ))
                    ) : (
                        <CommandPalette.FreeSearchAction />
                    )}
                </CommandPalette.Page>
            </CommandPalette>
        </>
    )
}

const useStyles = makeStyles(theme => ({
    btnSearch: {
        height: 45,
        textTransform: 'none',
        fontSize: 12,
        borderRadius: theme.borderRadius,
        paddingLeft: 5,
        paddingRight: 5,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'none',
        width: 200,
        border: theme.border,
        color: theme.primary,
        '&:hover': {
            background: 'none !important',
            outline: `1.5px solid ${theme.primary}`
        }
    },
    btnInfo: {
        display: 'flex'
    },
    mainBtnInfo: {
        display: 'flex',
        alignItems: 'center',
        fontSize: 12,
        borderRadius: 7,
        paddingLeft: 5,
        paddingRight: 5
    },
    additonalBtnInfo: {
        display: 'flex',
        background: theme.grayLight,
        fontSize: 13,
        borderRadius: 7,
        paddingLeft: 5,
        paddingRight: 5
    }
}))
